import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Paper,
} from '@mui/material'
import { useGetList } from 'react-admin'
import { client } from '../../providers/data/client'
import { formatDateToMMDDYYYY } from '../../utils/helpers'
import moment from 'moment'

const buildMonthOptions = () => {
  const options = []
  for (let i = 1; i <= 12; i++) {
    const m = moment().subtract(i, 'months')
    options.push({
      label: m.format('MMMM YYYY'),
      start: m.clone().startOf('month').format('YYYY-MM-DD'),
      end: m.clone().endOf('month').format('YYYY-MM-DD'),
    })
  }
  return options
}

const MONTH_OPTIONS = buildMonthOptions()

// Renders a table of kits for a single invoice section
const KitTable = ({ kits, amountField, dateField, dateLabel }) => (
  <Table size="small" sx={{ mb: 2 }}>
    <TableHead>
      <TableRow>
        <TableCell>Request ID</TableCell>
        <TableCell>Patient</TableCell>
        <TableCell>{dateLabel}</TableCell>
        <TableCell align="right">Fee / Kit</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {kits.map(k => (
        <TableRow key={k.requestId} hover>
          <TableCell sx={{ fontFamily: 'monospace' }}>{k.requestId}</TableCell>
          <TableCell>{[k.patientFirstName, k.patientLastName].filter(Boolean).join(' ') || '—'}</TableCell>
          <TableCell>{formatDateToMMDDYYYY(k[dateField]) || '—'}</TableCell>
          <TableCell align="right">${Number(k[amountField] || 0).toFixed(2)}</TableCell>
        </TableRow>
      ))}
      <TableRow>
        <TableCell colSpan={3} align="right"><strong>Subtotal</strong></TableCell>
        <TableCell align="right">
          <strong>${kits.reduce((sum, k) => sum + Number(k[amountField] || 0), 0).toFixed(2)}</strong>
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
)

// A single invoice block — used both in combined and split mode
const InvoiceSection = ({ title, shippedKits, testedKits, result, error }) => {
  const grandTotal = (
    shippedKits.reduce((sum, k) => sum + Number(k.providerShippedAmount || 0), 0) +
    testedKits.reduce((sum, k) => sum + Number(k.providerTestedAmount || 0), 0)
  ).toFixed(2)

  return (
    <Box sx={{ mb: 2 }}>
      {title && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>}

      {result && (
        <Alert severity="success" sx={{ mb: 1 }}>
          Draft Order created — <strong>${Number(result.totalAmount).toFixed(2)}</strong> ({result.shippedCount} shipped, {result.testedCount} tested).{' '}
          <a href={result.draftOrderAdminUrl} target="_blank" rel="noreferrer">View in Shopify</a>
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Kits to Invoice (Shipped) — {shippedKits.length} kit{shippedKits.length !== 1 ? 's' : ''}
      </Typography>
      {shippedKits.length === 0
        ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No shipped kits eligible for this period.</Typography>
        : <KitTable kits={shippedKits} amountField="providerShippedAmount" dateField="shippedDate" dateLabel="Shipped Date" />
      }

      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Kits to Invoice (Tested) — {testedKits.length} kit{testedKits.length !== 1 ? 's' : ''}
      </Typography>
      {testedKits.length === 0
        ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No tested kits eligible for invoicing.</Typography>
        : <KitTable kits={testedKits} amountField="providerTestedAmount" dateField="testedDate" dateLabel="Tested Date" />
      }

      <Box display="flex" justifyContent="flex-end">
        <Typography variant="body1"><strong>Total: ${grandTotal}</strong></Typography>
      </Box>
    </Box>
  )
}

const KitRequestInvoicingPage = () => {
  const [providerGroupId, setProviderGroupId] = useState('')
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0].start)
  const [splitByLocation, setSplitByLocation] = useState(false)
  const [loadedData, setLoadedData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [invoicing, setInvoicing] = useState(false)
  const [results, setResults] = useState({}) // keyed by locationId or 'combined'
  const [errors, setErrors] = useState({})
  const [globalError, setGlobalError] = useState(null)

  const { data: providerGroups, isLoading: groupsLoading } = useGetList('provider-groups', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  })

  const { data: locations } = useGetList('provider-locations', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'locationName', order: 'ASC' },
    filter: providerGroupId ? { providerGroupId } : {},
  }, { enabled: !!providerGroupId })

  // When provider group changes, default the split toggle from the group's setting
  useEffect(() => {
    if (!providerGroupId || !providerGroups) return
    const group = providerGroups.find(g => g.id === providerGroupId)
    if (group) setSplitByLocation(!!group.splitInvoiceByLocation)
  }, [providerGroupId, providerGroups])

  const selectedMonthOption = MONTH_OPTIONS.find(o => o.start === selectedMonth) || MONTH_OPTIONS[0]

  const resetState = () => {
    setLoadedData(null)
    setResults({})
    setErrors({})
    setGlobalError(null)
  }

  const handleLoad = async () => {
    if (!providerGroupId) return
    setLoading(true)
    resetState()

    try {
      const { start, end } = selectedMonthOption
      const res = await client().get(`kit-requests?_providerGroupId=${providerGroupId}&_pageSize=1000&_page=1`)
      const kits = res.data.data || []

      const shippedKits = kits.filter(k =>
        k.requestStatus !== 'canceled' &&
        k.shippedFinancialStatus === 'none' &&
        k.shippedDate &&
        k.shippedDate <= end
      )
      const testedKits = kits.filter(k =>
        k.requestStatus !== 'canceled' &&
        k.testedDate &&
        k.testedDate <= end &&
        k.testedFinancialStatus === 'none'
      )

      setLoadedData({ shippedKits, testedKits })
    } catch (err) {
      setGlobalError(err?.response?.data?.message || err.message || 'Failed to load kit requests.')
    } finally {
      setLoading(false)
    }
  }

  const callGenerate = async ({ providerLocationId, shopifyLocationName, shippedKits, testedKits }) => {
    const { start, end } = selectedMonthOption
    const res = await client().post('invoices/generate', {
      providerGroupId,
      billingPeriodStart: start,
      billingPeriodEnd: end,
      ...(providerLocationId ? { providerLocationId, shopifyLocationName } : {}),
    })
    return res.data
  }

  const handleCreateInvoice = async () => {
    if (!loadedData) return
    setInvoicing(true)
    setResults({})
    setErrors({})
    setGlobalError(null)

    try {
      if (!splitByLocation) {
        // Combined — single draft order for the whole group
        const result = await callGenerate({
          shippedKits: loadedData.shippedKits,
          testedKits: loadedData.testedKits,
        })
        setResults({ combined: result })
      } else {
        // Split — one draft order per location
        const locationList = locations || []
        const newResults = {}
        const newErrors = {}

        for (const loc of locationList) {
          const locShipped = loadedData.shippedKits.filter(k => k.providerLocationId === loc.id)
          const locTested = loadedData.testedKits.filter(k => k.providerLocationId === loc.id)
          if (locShipped.length === 0 && locTested.length === 0) continue

          try {
            const result = await callGenerate({
              providerLocationId: loc.id,
              shopifyLocationName: loc.shopifyLocation || undefined,
              shippedKits: locShipped,
              testedKits: locTested,
            })
            newResults[loc.id] = result
          } catch (err) {
            newErrors[loc.id] = err?.response?.data?.message || err.message || 'Failed to create invoice.'
          }
        }

        setResults(newResults)
        setErrors(newErrors)
      }

      // Reload kit data to reflect updated statuses, but preserve results/errors
      try {
        const res = await client().get(`kit-requests?_providerGroupId=${providerGroupId}&_pageSize=1000&_page=1`)
        const kits = res.data.data || []
        const { start, end } = selectedMonthOption
        const shippedKits = kits.filter(k =>
          k.requestStatus !== 'canceled' &&
          k.shippedFinancialStatus === 'none' &&
          k.shippedDate &&
          k.shippedDate <= end
        )
        const testedKits = kits.filter(k =>
          k.requestStatus !== 'canceled' &&
          k.testedDate &&
          k.testedDate <= end &&
          k.testedFinancialStatus === 'none'
        )
        setLoadedData({ shippedKits, testedKits })
      } catch (_) {
        // Non-critical — results are already shown
      }
    } catch (err) {
      setGlobalError(err?.response?.data?.message || err.message || 'Failed to create invoice.')
    } finally {
      setInvoicing(false)
    }
  }

  const hasEligibleKits = loadedData && (loadedData.shippedKits.length > 0 || loadedData.testedKits.length > 0)

  const grandTotal = loadedData
    ? (
        loadedData.shippedKits.reduce((s, k) => s + Number(k.providerShippedAmount || 0), 0) +
        loadedData.testedKits.reduce((s, k) => s + Number(k.providerTestedAmount || 0), 0)
      ).toFixed(2)
    : '0.00'

  const locationList = locations || []

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Typography variant="h5" gutterBottom>Kit Requests Invoicing</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a provider group and billing month to review eligible kit requests, then create a Shopify Draft Order for review.
      </Typography>

      {/* Step 1 — Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Step 1 — Select Group & Period</Typography>
        <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
          <FormControl sx={{ minWidth: 280 }}>
            <InputLabel>Provider Group</InputLabel>
            <Select
              value={providerGroupId}
              label="Provider Group"
              onChange={e => {
                setProviderGroupId(e.target.value)
                resetState()
              }}
              disabled={groupsLoading}
            >
              {(providerGroups || []).map(pg => (
                <MenuItem key={pg.id} value={pg.id}>{pg.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Billing Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Billing Month"
              onChange={e => {
                setSelectedMonth(e.target.value)
                resetState()
              }}
            >
              {MONTH_OPTIONS.map(o => (
                <MenuItem key={o.start} value={o.start}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={splitByLocation}
                onChange={e => {
                  setSplitByLocation(e.target.checked)
                  resetState()
                }}
                disabled={!providerGroupId}
              />
            }
            label="Split by Location"
          />

          <Button
            variant="contained"
            onClick={handleLoad}
            disabled={!providerGroupId || loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Loading…' : 'Load'}
          </Button>
        </Box>
      </Paper>

      {globalError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGlobalError(null)}>{globalError}</Alert>
      )}

      {/* Step 2 — Review */}
      {loadedData && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 2 — Review: {selectedMonthOption.label}
          </Typography>

          {!splitByLocation ? (
            // Combined view
            <InvoiceSection
              shippedKits={loadedData.shippedKits}
              testedKits={loadedData.testedKits}
              result={results.combined}
              error={errors.combined}
            />
          ) : (
            // Split by location view
            locationList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No locations found for this group.</Typography>
            ) : (
              locationList.map((loc, idx) => {
                const locShipped = loadedData.shippedKits.filter(k => k.providerLocationId === loc.id)
                const locTested = loadedData.testedKits.filter(k => k.providerLocationId === loc.id)
                if (locShipped.length === 0 && locTested.length === 0 && !results[loc.id] && !errors[loc.id]) return null
                return (
                  <Box key={loc.id}>
                    {idx > 0 && <Divider sx={{ my: 3 }} />}
                    <InvoiceSection
                      title={loc.locationName}
                      shippedKits={locShipped}
                      testedKits={locTested}
                      result={results[loc.id]}
                      error={errors[loc.id]}
                    />
                  </Box>
                )
              })
            )
          )}

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Grand Total: ${grandTotal}</Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={!hasEligibleKits || invoicing}
              onClick={handleCreateInvoice}
              startIcon={invoicing ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {invoicing
                ? 'Creating Invoice…'
                : splitByLocation
                  ? `Create ${locationList.filter(l =>
                      loadedData.shippedKits.some(k => k.providerLocationId === l.id) ||
                      loadedData.testedKits.some(k => k.providerLocationId === l.id)
                    ).length} Invoice(s)`
                  : 'Create Invoice'
              }
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  )
}

export default KitRequestInvoicingPage
